import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { SPINOFF_AUTH_CONFIG } from './spinoff-auth.config';
import { DEFAULT_AUTH_REFRESH_TIMEOUT_MS, SpinoffAuthService } from './spinoff-auth.service';

/**
 * accessToken이 있는 상태로 부팅할 때 auth.init()이 타는 loadMe() 경로에는
 * refresh()와 달리 rxjs timeout이 없다 — 네트워크가 멈추면 이 Promise가
 * 영구히 settle되지 않을 수 있다. 이 상한이 없으면 로그인 버튼을 부팅 완료
 * 전까지 disabled 처리하는 화면에서 사용자가 로그인 화면에 진입할 방법이
 * 전혀 없어진다. refresh()의 기본 타임아웃(`SPINOFF_AUTH_CONFIG.refreshTimeoutMs`,
 * KEDA cold-start 예산 근거)과 같은 값을 재사용한다 — 앱이 그 값을 오버라이드하면
 * 이 부트 게이트도 같이 따라간다(2026-08-11, apps/sbe-sms/user-web 로컬 버전이
 * 이 상수를 매직 넘버로 복제해 오버라이드가 반영되지 않던 문제를 lib화하며 해소).
 */
@Injectable({ providedIn: 'root' })
export class SpinoffBootGateService {
  private readonly auth = inject(SpinoffAuthService);
  private readonly config = inject(SPINOFF_AUTH_CONFIG);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _completed = signal(false);
  private initPromise: Promise<void> | null = null;

  /**
   * 앱 최초 부팅 시 인증 상태 확인(auth.init())이 완료됐는지를 앱 전체에서
   * 한 번만 추적한다. `SpinoffAuthService.ready()`는 로그아웃 시 다시 false로
   * 리셋되지만(라이브러리 내부 동작), 이 게이트는 최초 1회 완료 후 계속 true를
   * 유지해 로그아웃마다 로딩 UI가 재노출되는 깜빡임을 막는다.
   */
  readonly completed = this._completed.asReadonly();

  ensureStarted(): void {
    if (this._completed() || this.initPromise) {
      return;
    }
    if (!this.isBrowser) {
      // SSR/build-time prerender(SSG) 환경에는 실제 사용자 쿠키가 없다 — auth.init()이
      // 여기서 그대로 돌면 빌드 프로세스가 백엔드로 실제 네트워크 요청(/auth/refresh)을
      // 보내는데, 그 시점에 백엔드가 아직 안 떠 있거나 응답이 늦으면 SpinoffAuthService가
      // infraError를 세우고 그 상태가 정적 HTML에 그대로 구워진다(app-shell이 라우트
      // 콘텐츠 전체를 오류 카드로 덮으므로) — prerender된 공개 페이지가 통째로 오류
      // 화면으로 배포되는 사고로 이어진다. SSR에서는 항상 미인증으로 완료 처리하고,
      // 브라우저에서 hydration 후 다시 ensureStarted()가 정상 경로로 실행된다.
      this._completed.set(true);
      return;
    }
    if (this.auth.ready()) {
      this._completed.set(true);
      return;
    }
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutMs = this.config.refreshTimeoutMs ?? DEFAULT_AUTH_REFRESH_TIMEOUT_MS;
    const timeout = new Promise<void>((resolve) => {
      timeoutId = setTimeout(resolve, timeoutMs);
    });
    this.initPromise = Promise.race([this.auth.init().catch(() => undefined), timeout]).finally(() => {
      clearTimeout(timeoutId);
      this._completed.set(true);
    });
  }
}
